/**
 * SALDOC · Al perderse la sesión, la pestaña vuelve a la portada y no queda
 * ningún documento cargado (OBS-AUTH-AL-CERRAR-SESION-EL-DOCUMENTO-SIGUE-CARGADO).
 *
 * DOP-0 (a): vale para todo cierre — botón «Cerrar sesión», caducidad y cierre
 * en otra pestaña —, porque los tres llegan como `user` → null.
 * DOP-1 (a): se borra todo lo medido con datos del documento o de la cuenta
 * (.ptel/p0_saldoc_20261019/AUDIT_SALDOC.md §2); se conservan las preferencias.
 * DOP-2 (a): sin pregunta previa; aviso después, ya en la portada.
 *
 * Solo cuenta una sesión VERIFICADA: el login con correo sin verificar, el
 * registro y el reenvío de verificación pasan un instante por usuario → null
 * (`signOut` interno) y no deben mover a nadie de pantalla.
 *
 * El vaciado va en el efecto (no en el escuchador de Firebase) a propósito: los
 * efectos de los hijos corren antes que los del padre, así que cualquier
 * guardado del asistente en ese mismo ciclo ya ha ocurrido cuando se borra.
 *
 * @module hooks/useVaciadoAlPerderSesion
 */

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import type { User as FirebaseUser } from 'firebase/auth'
import { vaciarCacheNominatim } from '../services/geocoding/cache/NominatimCache'

/** Documento del asistente (useDocumentCache), filtros del mapa (useFilterState)
 *  y registros de coordenadas (coordinateStore). */
export const CLAVES_SESSION_STORAGE = [
  'ptel-wizard-cache',
  'ptel-filter-state',
  'ptel-coordinate-store',
] as const

/** Filtros del mapa de P2 (MapViewer): `ptel-filters` o `ptel-filters-<INE>`;
 *  `searchText` lleva lo tecleado en el buscador. Medido en producción tras la
 *  V del 19-oct-2026 (AUDIT_SALDOC §11): el censo inicial no la recogía. */
export const PREFIJOS_SESSION_STORAGE = ['ptel-filters'] as const

/** Marca de sesión y nombre o correo de la cuenta (AuthContext.login). */
export const CLAVES_LOCAL_STORAGE = [
  'ptel_authenticated',
  'ptel_auth_date',
  'ptel_user',
] as const

/** Cachés del service worker con las consultas hechas (vite.config.ts). */
export const CACHES_DE_CONSULTAS = ['cartociudad-api-v1', 'idee-api-v1'] as const

const CLAVE_DOCUMENTO = 'ptel-wizard-cache'

export const AVISO_SESION_CERRADA = 'Sesión cerrada'
export const AVISO_DOCUMENTO_RETIRADO = 'Se ha retirado el documento de esta pestaña.'

/**
 * Borra de sessionStorage y localStorage lo que lleva datos del documento o de
 * la cuenta. Devuelve si había un documento guardado en la pestaña.
 */
export function vaciarAlmacenesDeSesion(): boolean {
  let habiaDocumento = false
  try {
    habiaDocumento = sessionStorage.getItem(CLAVE_DOCUMENTO) !== null
  } catch (error) {
    console.warn('[SALDOC] No se pudo leer sessionStorage:', error)
  }
  let conPrefijo: string[] = []
  try {
    conPrefijo = Object.keys(sessionStorage).filter(clave =>
      PREFIJOS_SESSION_STORAGE.some(prefijo => clave.startsWith(prefijo)))
  } catch (error) {
    console.warn('[SALDOC] No se pudieron listar las claves de sessionStorage:', error)
  }
  for (const clave of [...CLAVES_SESSION_STORAGE, ...conPrefijo]) {
    try { sessionStorage.removeItem(clave) } catch (error) {
      console.warn(`[SALDOC] No se pudo borrar ${clave} de sessionStorage:`, error)
    }
  }
  for (const clave of CLAVES_LOCAL_STORAGE) {
    try { localStorage.removeItem(clave) } catch (error) {
      console.warn(`[SALDOC] No se pudo borrar ${clave} de localStorage:`, error)
    }
  }
  // NOMFIX-a2: la caché de Nominatim lleva nombres de infraestructuras del
  // documento. Vive en memoria del módulo (no se recarga la pestaña) y en
  // sessionStorage: se vacían las dos.
  try { vaciarCacheNominatim() } catch (error) {
    console.warn('[SALDOC] No se pudo vaciar la caché de Nominatim:', error)
  }
  return habiaDocumento
}

/** Borra las cachés del service worker que guardan consultas con direcciones. */
export async function borrarCachesDeConsultas(): Promise<void> {
  if (typeof caches === 'undefined') return
  await Promise.all(
    CACHES_DE_CONSULTAS.map(nombre =>
      caches.delete(nombre).catch(error => {
        console.warn(`[SALDOC] No se pudo borrar la caché ${nombre}:`, error)
        return false
      }),
    ),
  )
}

/**
 * Vigila la sesión: cuando una sesión verificada pasa a null, vacía la pestaña,
 * vuelve a la portada y avisa. Debe montarse dentro del enrutador.
 */
export function useVaciadoAlPerderSesion(user: FirebaseUser | null): void {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const habiaSesionRef = useRef(false)

  useEffect(() => {
    const habiaSesion = habiaSesionRef.current
    habiaSesionRef.current = !!user?.emailVerified
    if (!habiaSesion || user) return

    const habiaDocumento = vaciarAlmacenesDeSesion()
    void borrarCachesDeConsultas()
    if (pathname !== '/') navigate('/', { replace: true })
    toast.info(
      AVISO_SESION_CERRADA,
      habiaDocumento ? { description: AVISO_DOCUMENTO_RETIRADO } : undefined,
    )
  }, [user, pathname, navigate])
}
